import { useQuery } from '@tanstack/react-query'
import {
  Bot,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Globe,
  Brain,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { seoAnalytics } from '@/lib/api'

interface BotSummaryCardsProps {
  days: number
  section?: string
}

function StatCard({
  title,
  value,
  change,
  changeType,
  icon: Icon,
  iconColor,
  iconBg,
  isLoading,
}: {
  title: string
  value: string | number
  change?: string
  changeType?: 'up' | 'down' | 'neutral'
  icon: any
  iconColor: string
  iconBg: string
  isLoading?: boolean
}) {
  if (isLoading) {
    return (
      <div className="stat-card">
        <div className="flex items-start justify-between">
          <div className="space-y-3 flex-1">
            <div className="skeleton h-4 w-24" />
            <div className="skeleton h-8 w-16" />
            <div className="skeleton h-4 w-32" />
          </div>
          <div className={cn('flex items-center justify-center w-12 h-12 rounded-2xl', iconBg)}>
            <Icon className={cn('w-6 h-6', iconColor)} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="stat-card group hover:shadow-soft-lg transition-all duration-300">
      <div className="flex items-start justify-between">
        <div className="space-y-3">
          <p className="stat-label">{title}</p>
          <p className="stat-value">{value}</p>
          {change && (
            <div
              className={cn(
                'stat-change',
                changeType === 'up' && 'stat-change-up',
                changeType === 'down' && 'stat-change-down',
                changeType === 'neutral' && 'text-slate-500'
              )}
            >
              {changeType === 'up' && <TrendingUp className="w-3.5 h-3.5" />}
              {changeType === 'down' && <TrendingDown className="w-3.5 h-3.5" />}
              <span>{change}</span>
            </div>
          )}
        </div>
        <div
          className={cn(
            'flex items-center justify-center w-12 h-12 rounded-2xl transition-transform group-hover:scale-110',
            iconBg
          )}
        >
          <Icon className={cn('w-6 h-6', iconColor)} />
        </div>
      </div>
    </div>
  )
}

export default function BotSummaryCards({ days, section }: BotSummaryCardsProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['seo-bot-summary', days, section],
    queryFn: () => seoAnalytics.getBotSummary(days, section),
  })

  const summary = data?.data

  // Calculate totals by bot type from by_bot array
  const totalHits = summary?.by_bot?.reduce((acc: number, b) => acc + (b.total_hits || 0), 0) || 0
  const uniqueBots = summary?.by_bot?.length || 0

  const searchEngineHits = summary?.by_bot?.filter((b) => b.bot_type === 'search_engine')
    .reduce((acc: number, b) => acc + (b.total_hits || 0), 0) || 0

  const aiBotsHits = summary?.by_bot?.filter((b) => b.bot_type === 'ai')
    .reduce((acc: number, b) => acc + (b.total_hits || 0), 0) || 0

  const totalErrors = summary?.by_bot?.reduce((acc: number, b) => acc + (b.errors_404 || 0), 0) || 0

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
      <StatCard
        title="Total Rastreos"
        value={totalHits.toLocaleString()}
        change={`${uniqueBots} bots activos`}
        changeType="neutral"
        icon={Bot}
        iconColor="text-primary-600 dark:text-primary-400"
        iconBg="bg-primary-100 dark:bg-primary-900/30"
        isLoading={isLoading}
      />

      <StatCard
        title="Buscadores"
        value={searchEngineHits.toLocaleString()}
        change="Googlebot, Bingbot..."
        changeType="neutral"
        icon={Globe}
        iconColor="text-success-600 dark:text-success-400"
        iconBg="bg-success-100 dark:bg-success-900/30"
        isLoading={isLoading}
      />

      <StatCard
        title="Bots IA"
        value={aiBotsHits.toLocaleString()}
        change="GPTBot, ClaudeBot..."
        changeType="neutral"
        icon={Brain}
        iconColor="text-accent-600 dark:text-accent-400"
        iconBg="bg-accent-100 dark:bg-accent-900/30"
        isLoading={isLoading}
      />

      <StatCard
        title="Errores 404"
        value={totalErrors.toLocaleString()}
        change={totalErrors > 10 ? 'Revisar URLs rotas' : 'Estado OK'}
        changeType={totalErrors > 10 ? 'down' : 'up'}
        icon={AlertTriangle}
        iconColor="text-warning-600 dark:text-warning-400"
        iconBg="bg-warning-100 dark:bg-warning-900/30"
        isLoading={isLoading}
      />
    </div>
  )
}
